'use client';

import { useEffect, useId, useRef, useState } from 'react';

import { TenantBrand } from '@/components/brand/tenant-brand';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

// #2535: the uniform resend pacing shown to every requester. The server's
// retry_after derives only from public submission history (60s per email,
// IP window); this 300s floor mirrors the private delivery-side reissue
// cooldown without ever reading it, so the countdown cannot classify
// accounts. A longer server window (IP throttle) extends it via max().
export const RESEND_COOLDOWN_SECONDS = 300;

// Ceiling on a server-supplied retry_after: a buggy value (milliseconds
// instead of seconds) must not lock resend for days. The longest legitimate
// public window (IP throttle) is 10 minutes, so an hour is generous.
const RESEND_MAX_SECONDS = 3600;

export const START_INVALID_EMAIL_MESSAGE =
  'Enter the email address you use in Circle.';
export const START_GENERIC_ERROR =
  'Something went wrong on our side. Try again in a moment.';

const SUPPORT_EMAIL = 'support@builttoclose.ai';

// Mirrors the server's normalizeAccountSetupRequestEmail shape check so a
// malformed address fails locally with guidance instead of a round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REQUEST_REF_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type Phase = 'form' | 'sent';

// A 202 is always the uniform accepted shape, but the client must not trust
// that: a malformed body still renders the identical sent state, degrading
// to the floor countdown and omitting the reference line. An exception-path
// branch that rendered differently would be a distinguisher if it ever
// correlated with account state.
function parseAccepted(body: unknown): { requestRef: string | null; retryAfter: number } {
  const candidate =
    body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const requestRef =
    typeof candidate.request_ref === 'string'
      && REQUEST_REF_PATTERN.test(candidate.request_ref)
      ? candidate.request_ref
      : null;
  const retryAfter =
    Number.isSafeInteger(candidate.retry_after)
      && (candidate.retry_after as number) >= 0
      ? (candidate.retry_after as number)
      : 0;
  return { requestRef, retryAfter };
}

function formatRemaining(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

export function StartForm({
  reason = null,
  finishSetupRecoveryAvailable = false,
  joinUrl,
  brandName = null,
  logoLightUrl = null,
  logoDarkUrl = null,
  appliedTheme = 'dark',
}: {
  // 'finish-setup' is the only allowlisted reason; the page passes null for
  // anything else so query-string content is never echoed.
  reason?: 'finish-setup' | null;
  finishSetupRecoveryAvailable?: boolean;
  joinUrl: string;
  brandName?: string | null;
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  appliedTheme?: 'light' | 'dark';
}) {
  const emailId = useId();
  const errorId = useId();
  const recoveryAvailable =
    reason === 'finish-setup' && finishSetupRecoveryAvailable;

  const [email, setEmail] = useState('');
  const [recoveryEmailFallback, setRecoveryEmailFallback] = useState(false);
  const usePrivateRecovery = recoveryAvailable && !recoveryEmailFallback;
  // The address the accepted request actually carried. The input stays
  // mounted (and editable) while the POST is in flight, so the sent screen
  // and any resend must use this snapshot, not the live input value.
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>('form');
  const [requestRef, setRequestRef] = useState<string | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  // Deadline-based countdown: the deadline is fixed from Date.now() at
  // response receipt and the remaining time is re-derived on every tick and
  // on visibilitychange. Background tabs throttle intervals to >=1/min; a
  // decrement-per-tick counter would drift stale there, while a re-derived
  // delta self-corrects the moment any tick or visibility change fires.
  // Only local deltas are involved, so client/server clock skew is
  // irrelevant.
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const previousRecoveryAvailable = useRef(recoveryAvailable);
  const requestEpoch = useRef(0);

  const remaining =
    phase === 'sent' && deadline !== null
      ? Math.max(0, Math.ceil((deadline - now) / 1000))
      : 0;
  const expired = phase === 'sent' && remaining === 0;

  // The focused submit button unmounts with the form on the phase flip;
  // without explicit focus management keyboard and screen-reader users are
  // dropped to <body>. Landing on the status region also re-announces it.
  useEffect(() => {
    if (phase === 'sent') statusRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (previousRecoveryAvailable.current === recoveryAvailable) return;
    previousRecoveryAvailable.current = recoveryAvailable;
    requestEpoch.current += 1;
    setRecoveryEmailFallback(false);
    setSubmittedEmail(null);
    setError(null);
    setLoading(false);
    setPhase('form');
    setRequestRef(null);
    setDeadline(null);
    setNow(0);
  }, [recoveryAvailable]);

  useEffect(() => {
    if (phase !== 'sent' || deadline === null || expired) return;
    const update = () => setNow(Date.now());
    const intervalId = setInterval(update, 1000);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', update);
    };
  }, [phase, deadline, expired]);

  async function requestLink() {
    // Re-entry guard beyond the disabled button: a re-dispatched submit
    // (Enter-key implicit submission, double-click) during the in-flight
    // window must not fire a second request.
    if (loading) return;
    setError(null);

    // A resend re-targets the accepted address; the live input value is only
    // consulted while the form is still showing.
    const trimmed = phase === 'sent' && submittedEmail !== null
      ? submittedEmail
      : email.trim();
    if (!usePrivateRecovery) {
      if (
        trimmed.length === 0
        || trimmed.length > 320
        || !EMAIL_PATTERN.test(trimmed)
      ) {
        setError(START_INVALID_EMAIL_MESSAGE);
        return;
      }
    }

    setLoading(true);
    const activeRequestEpoch = requestEpoch.current + 1;
    requestEpoch.current = activeRequestEpoch;
    try {
      const res = await fetch(
        usePrivateRecovery
          ? '/api/onboarding/finish-setup-link'
          : '/api/onboarding/setup-link',
        usePrivateRecovery
          ? { method: 'POST' }
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: trimmed }),
            }
      );
      if (requestEpoch.current !== activeRequestEpoch) return;
      if (res.status === 202) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          // Uniform degradation: see parseAccepted.
        }
        if (requestEpoch.current !== activeRequestEpoch) return;
        const accepted = parseAccepted(body);
        const nowMs = Date.now();
        setSubmittedEmail(usePrivateRecovery ? null : trimmed);
        setRequestRef(accepted.requestRef);
        setDeadline(
          nowMs
            + Math.max(
              RESEND_COOLDOWN_SECONDS,
              Math.min(accepted.retryAfter, RESEND_MAX_SECONDS)
            ) * 1000
        );
        setNow(nowMs);
        setPhase('sent');
        return;
      }
      // 400 is the endpoint's only input rejection; everything else (503,
      // unexpected statuses) gets one fixed retryable message. Neither
      // branch depends on account state — the server does not know it here.
      // In the sent phase there is no input to correct, so even a 400 maps
      // to the retryable copy there.
      setError(
        !usePrivateRecovery && res.status === 400 && phase === 'form'
          ? START_INVALID_EMAIL_MESSAGE
          : START_GENERIC_ERROR
      );
    } catch {
      if (requestEpoch.current !== activeRequestEpoch) return;
      setError(START_GENERIC_ERROR);
    } finally {
      if (requestEpoch.current === activeRequestEpoch) setLoading(false);
    }
  }

  const isValidationError = error === START_INVALID_EMAIL_MESSAGE;
  const helpSubject = requestRef
    ? `Account setup help (request reference ${requestRef})`
    : 'Account setup help';
  const helpHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(helpSubject)}`;

  return (
    <main
      id="main"
      className="flex min-h-screen flex-col items-center justify-center px-4 py-10"
    >
      <Card className="w-full max-w-md gap-6 p-6 shadow-lg sm:p-8">
        <div className="flex flex-col items-center space-y-2 text-center">
          <TenantBrand
            size="sm"
            brandName={brandName}
            logoLightUrl={logoLightUrl}
            logoDarkUrl={logoDarkUrl}
            theme={appliedTheme}
          />
          <h1 className="text-3xl font-extrabold uppercase tracking-tight">
            Start your training
          </h1>
          <p className="text-sm text-muted-foreground">
            {usePrivateRecovery
              ? 'Request a secure link to finish setting up your account.'
              : 'Enter the email you use in Circle and we\'ll send your secure account setup link.'}
          </p>
        </div>

        {reason === 'finish-setup' && phase === 'form' && (
          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm space-y-1">
            <p className="font-medium">Your account setup isn&apos;t finished yet.</p>
            <p className="text-muted-foreground">
              {usePrivateRecovery
                ? 'Passwords, Google, and magic links only work after setup is complete. Request a fresh setup link to finish.'
                : 'Passwords, Google, and magic links only work after setup is complete. Enter the email you use in Circle and we\'ll send you a fresh setup link to finish.'}
            </p>
          </div>
        )}

        {error && (
          <p
            id={errorId}
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        {phase === 'sent' ? (
          <div className="space-y-4">
            <div
              role="status"
              ref={statusRef}
              tabIndex={-1}
              className="rounded-lg border border-border bg-muted/40 p-4 text-center space-y-2"
            >
              <p className="text-base font-medium">Check your email</p>
              <p className="text-base text-muted-foreground">
                {usePrivateRecovery ? (
                  <>If your account still needs setup, a link is on its way to the email on your account. It can take a couple of minutes to arrive — check spam too.</>
                ) : (
                  <>If this email matches a course enrollment, a setup link is on its way to{' '}
                    <span className="font-medium text-foreground">{submittedEmail}</span>.
                    It can take a couple of minutes to arrive — check spam too.</>
                )}
              </p>
            </div>

            {/* Outside the status region: a status region politely announces
                all of its content, which would override this element's own
                aria-live="off" and read the countdown every second. */}
            {remaining > 0 && (
              <p
                role="timer"
                aria-live="off"
                className="text-center text-sm text-muted-foreground"
              >
                You can send it again in {formatRemaining(remaining)}.
              </p>
            )}
            {/* One-time polite announcement when resend unlocks; the ticking
                itself stays silent. */}
            <p aria-live="polite" className="text-center text-sm text-muted-foreground">
              {expired ? 'You can send it again now.' : ''}
            </p>

            <Button
              type="button"
              size="xl"
              className="w-full"
              disabled={loading || remaining > 0}
              onClick={() => void requestLink()}
            >
              {loading ? 'Sending…' : 'Send it again'}
            </Button>
          </div>
        ) : usePrivateRecovery ? (
          <div className="space-y-3" aria-busy={loading}>
            <Button
              type="button"
              size="xl"
              className="w-full"
              disabled={loading}
              onClick={() => void requestLink()}
            >
              {loading ? 'Sending…' : 'Send my setup link'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xl"
              className="w-full"
              disabled={loading}
              onClick={() => {
                setError(null);
                setRecoveryEmailFallback(true);
              }}
            >
              Use a different email
            </Button>
          </div>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void requestLink();
            }}
            className="space-y-4"
            aria-busy={loading}
            // Custom validation only: the styled role="alert" error with
            // aria-invalid/aria-describedby wiring is the single validation
            // path, instead of the browser bubble on some inputs and ours on
            // others.
            noValidate
          >
            <div className="space-y-2">
              <label htmlFor={emailId} className="text-sm font-medium">
                Email you use in Circle
              </label>
              <Input
                id={emailId}
                size="xl"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={320}
                autoComplete="email"
                autoFocus
                aria-invalid={isValidationError ? true : undefined}
                aria-describedby={error ? errorId : undefined}
              />
            </div>

            <Button type="submit" size="xl" className="w-full" disabled={loading}>
              {loading ? 'Sending…' : 'Email me a setup link'}
            </Button>
          </form>
        )}

        <div className="space-y-1 text-center text-sm text-muted-foreground">
          <p>
            Already set up?{' '}
            <a href="/login" className="font-medium underline underline-offset-2">
              Sign in
            </a>
          </p>
          <p>
            Not enrolled?{' '}
            <a
              href={joinUrl}
              className="font-medium underline underline-offset-2"
            >
              Join The Elliott Group
            </a>
          </p>
          <p>
            <a href={helpHref} className="font-medium underline underline-offset-2">
              Get help
            </a>
            {requestRef && (
              <>
                {' '}
                — include request reference{' '}
                <span className="break-all font-mono text-xs">{requestRef}</span>
              </>
            )}
          </p>
        </div>
      </Card>
    </main>
  );
}
